import { User } from '@remarkable/ark-icons';
import { Divider, Typography } from '@remarkable/ark-web';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { type FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import { useDeleteUser } from 'src/api/queries/enterpriseAdmin';
import { Button, Input, Modal } from 'src/components';

export function ConfirmDeletionModal({
  member,
  closeModal,
  handleDeletion,
}: {
  member: FleetUser;
  closeModal: () => void;
  handleDeletion: () => void;
}) {
  const deleteUser = useDeleteUser();
  const [confirmInput, setConfirmInput] = useState('');
  const isConfirmed = confirmInput.toLowerCase() === 'delete';

  const handleConfirmClick = () => {
    setConfirmInput('');
    closeModal();
    handleDeletion();
  };

  return (
    <Modal.Content title="Delete user?">
      <div className="flex flex-col gap-y-24">
        <Typography
          variant="body-lg"
          as="div"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <User size={24} className="mr-4 inline" />
          <b>{member.name}</b> <span>({member.email})</span>
        </Typography>
        <Divider />
        <Typography variant="body-md" as="div" className="text-left">
          Here’s what will happen:
          <ul className="mt-20 ml-16 flex list-outside list-disc flex-col items-start text-left">
            <li>The account will be deleted within 7 days</li>
            <li>The user will no longer be able to log into this account</li>
            <li>
              The user will be removed from your overview once the deletion is
              complete
            </li>
          </ul>
        </Typography>
        <Typography variant="body-md" as="div" className="text-left">
          <strong>Please note:</strong> data connected to this user will not be
          retrievable after deletion.
        </Typography>
        <Input
          label={
            <Typography variant="body-md" className="text-left">
              Please type <b>Delete</b> to confirm
            </Typography>
          }
          value={confirmInput}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setConfirmInput(e.currentTarget.value)
          }
          name="confirm-deletion"
          data-cy="confirm-deletion"
          className="w-full"
          required
          maxLength={150}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (isConfirmed) {
                handleConfirmClick();
              }
            }
          }}
        />
        <div className="flex justify-center gap-16">
          <Modal.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Modal.Close>

          <Button
            variant="primary"
            loading={deleteUser.isPending}
            disabled={!isConfirmed}
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
}
